/**
 * The Share dialog (spec 015): after a publish, Maya copies the player link,
 * an embed snippet she can paste into Moodle or Canvas, and the integration
 * URL for Alex. It opens from the "Published!" banner's "Share…" and from the
 * publish badge's "Share…" (spec 010), never on its own, and makes no API
 * call: the URLs come from the publish response or `GET /stories/:id/published`.
 *
 * Copy follows 010's clipboard rule: when the clipboard refuses, the text is
 * focused and selected in its field for a manual copy.
 */

import React, { useEffect, useRef, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import type { Orientation } from '../../../Shared/src/@types';
import styles from './ShareDialog.module.scss';

/** The embed iframe's size per the story's preview orientation (spec 015 Decisions). */
export const EMBED_SIZES: Record<Orientation, { width: number; height: number }> = {
  portrait: { width: 375, height: 667 },
  landscape: { width: 667, height: 375 },
  desktop: { width: 640, height: 360 },
};

const escapeAttr = (s: string) =>
  s.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/** The iframe snippet for an LMS page. */
export function embedSnippet(playerUrl: string, orientation: Orientation, storyName: string): string {
  const { width, height } = EMBED_SIZES[orientation] ?? EMBED_SIZES.portrait;
  return `<iframe src="${escapeAttr(playerUrl)}" width="${width}" height="${height}" style="border:0" `
    + `allow="autoplay; fullscreen" title="${escapeAttr(storyName)}"></iframe>`;
}

export interface ShareDialogProps {
  show: boolean;
  onClose: () => void;
  storyName: string;
  /** The published player page of this version (spec 011). */
  playerUrl: string;
  /** The integration (capability) URL, `/external/:token` (spec 011). */
  capabilityUrl: string;
  /** The story's preview orientation; sizes the embed snippet. */
  orientation: Orientation;
  version?: number;
}

type RowKey = 'link' | 'embed' | 'integration';

interface RowProps {
  rowKey: RowKey;
  label: string;
  hint: string;
  value: string;
  multiline?: boolean;
  copied: boolean;
  failed: boolean;
  onCopy: (key: RowKey, value: string, field: HTMLInputElement | HTMLTextAreaElement | null) => void;
  extra?: React.ReactNode;
}

const ShareRow: React.FC<RowProps> = ({ rowKey, label, hint, value, multiline, copied, failed, onCopy, extra }) => {
  const fieldRef = useRef<HTMLInputElement & HTMLTextAreaElement>(null);
  const id = `share-${rowKey}`;
  const select = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => e.target.select();
  return (
    <div className={styles.row} data-testid={`share-row-${rowKey}`}>
      <label htmlFor={id} className={styles.label}>{label}</label>
      <p className={styles.hint}>{hint}</p>
      <div className={styles.field}>
        {multiline ? (
          <textarea id={id} ref={fieldRef} readOnly value={value} rows={3} className={styles.input} onFocus={select} data-testid={`share-${rowKey}-value`} />
        ) : (
          <input id={id} ref={fieldRef} type="text" readOnly value={value} className={styles.input} onFocus={select} data-testid={`share-${rowKey}-value`} />
        )}
        <div className={styles.actions}>
          <Button size="sm" variant="primary" onClick={() => onCopy(rowKey, value, fieldRef.current)} aria-label={`Copy ${label.toLowerCase()}`}>
            {copied ? 'Copied!' : 'Copy'}
          </Button>
          {extra}
        </div>
      </div>
      {failed && (
        <p className={styles.failed} role="status">Copy didn't work here. The text is selected: press Ctrl+C (⌘C on a Mac).</p>
      )}
    </div>
  );
};

const ShareDialog: React.FC<ShareDialogProps> = ({ show, onClose, storyName, playerUrl, capabilityUrl, orientation, version }) => {
  const [copied, setCopied] = useState<RowKey | null>(null);
  const [failed, setFailed] = useState<RowKey | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!show) {
      setCopied(null);
      setFailed(null);
    }
  }, [show]);
  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current);
  }, []);

  const onCopy: RowProps['onCopy'] = (key, value, field) => {
    const showSelected = () => {
      setCopied(null);
      setFailed(key);
      if (field) {
        field.focus();
        field.select();
      }
    };
    let pending: Promise<void>;
    try {
      if (!navigator.clipboard?.writeText) throw new Error('Clipboard unavailable');
      pending = Promise.resolve(navigator.clipboard.writeText(value));
    } catch {
      showSelected();
      return;
    }
    pending.then(() => {
      setFailed(null);
      setCopied(key);
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => setCopied(null), 2000);
    }, showSelected);
  };

  const embed = embedSnippet(playerUrl, orientation, storyName);
  const size = EMBED_SIZES[orientation] ?? EMBED_SIZES.portrait;

  return (
    <Modal show={show} onHide={onClose} centered size="lg" data-testid="share-dialog" aria-labelledby="share-dialog-title">
      <Modal.Header closeButton>
        <Modal.Title id="share-dialog-title">Share{version ? ` · v${version}` : ''}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <ShareRow
          rowKey="link"
          label="Player link"
          hint="Anyone with this link can play the story in a browser."
          value={playerUrl}
          copied={copied === 'link'}
          failed={failed === 'link'}
          onCopy={onCopy}
          extra={(
            <a className="btn btn-outline-secondary btn-sm" href={playerUrl} target="_blank" rel="noopener noreferrer" data-testid="share-open">
              Open ↗
            </a>
          )}
        />
        <ShareRow
          rowKey="embed"
          label="Embed code"
          hint={`Paste into your LMS page (HTML or embed mode). ${size.width}×${size.height}, ${orientation}.`}
          value={embed}
          multiline
          copied={copied === 'embed'}
          failed={failed === 'embed'}
          onCopy={onCopy}
        />
        <ShareRow
          rowKey="integration"
          label="Integration URL"
          hint="For developers: returns the published story's manifest and files as JSON."
          value={capabilityUrl}
          copied={copied === 'integration'}
          failed={failed === 'integration'}
          onCopy={onCopy}
        />
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>Done</Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ShareDialog;
